import _ from 'lodash'
import moment from 'moment'
import axios, { AxiosInstance } from 'axios'
import axiosRetry from 'axios-retry'
import { logAxiosError, logAxiosRequests } from '@micromart/kiosk-shared/lib/axios-error-handler'
import { User } from '@micromart/kiosk-shared/types'
// import { LogClass, LogMethod } from '@micromart/ts-utils'
import { ThrottleMethod } from '@micromart/ts-utils/decorators/throttle'
import ApiEvents from '@micromart/kiosk-app-shared/lib/api.events'
import centralEventHub from '@micromart/kiosk-app-shared/lib/centralEventHub'
import { decorators } from '@micromart/kiosk-app-shared/lib/segment'

// import Logger from './logger'
import config from './config'
import kiosk from './kiosk'

const { TrackWithSegment } = decorators

// const logger = Logger.child({ component: 'APIClient' })

// @LogClass(logger)
class API extends ApiEvents {
	private client: AxiosInstance
	private sessionId: string
	private session: any
	private tabletId: string
	private tabletUser: User

	constructor() {
		super()
		const host = config.fridge.host

		this.client = axios.create({
			baseURL: host,
			withCredentials: true,
		})

		// @ts-ignore
		axiosRetry(this.client, {
			retries: 3,
			shouldResetTimeout: true,
			retryCondition: (_error) => true,
			retryDelay: (retryCount) => 1000 * retryCount,
		})

		// logAxiosError(this.client, logger)
		// logAxiosRequests(this.client, logger)
	}

	// TODO: need to retrieve all cabinets at site
	@TrackWithSegment
	// @LogMethod
	public async register(data: any) {
		const url = '/api/gen2_tablet/register'
		const response: any = await this.client.post(url, data)

		const sessionId = response.headers['x-session-id']
		// logger.info('sessionId', { sessionId })

		this.client.defaults.headers.common = {
			'x-session-id': sessionId,
		}

		this.sessionId = sessionId

		const session = response.data
		const tablet = session?.tablet
		this.session = session
		this.tabletId = tablet?.id
		this.tabletUser = {
			accountId: session?.account_id,
			username: session?.user?.username,
		}

		this.emitRegistered(this.session)
		centralEventHub.emitRegistered(this.session)

		return session
	}

	@ThrottleMethod({ size: 1, queue: true })
	// @LogMethod
	public async registerDevice(force = false) {
		if (!force && this.tabletUser?.accountId) return
		const details = await kiosk.getDeviceDetails() as any
		details.emmConfig = _.cloneDeep(details)
		details.frontendConfig = _.omit(config, ['server.getHost'])
		details.frontendConfig.devicePixelRatio = window.devicePixelRatio
		return await this.register(details)
	}

	// @LogMethod
	public async updateTopDisplay(cabinetId: number, videoUrl: string, playOnce: boolean, playAt: Date = new Date()) {
		await this.registerDevice()
		const url = `/api/cabinets/${cabinetId}/top_display`
		const data = {
			top_display_url: videoUrl,
			play_once: playOnce,
			play_at: playAt,
		}
		const response = await this.client.put(url, data)
		return response.data
	}

	// @LogMethod
	public async activatePromo(voucherId: number) {
		await this.registerDevice()
		const url = `/api/vouchers/${voucherId}/unsafe_update`
		const data = {
			activated_at: new Date(),
		}
		const response = await this.client.put(url, data)
		return response.data
	}

	public async deactivatePromo(voucherId: number) {
		await this.registerDevice()
		const url = `/api/vouchers/${voucherId}/unsafe_update`
		const data = {
			activated_at: null,
		}
		const response = await this.client.put(url, data)
		return response.data
	}

	public async validateCanUpdateBooth() {
		const canUpdate = await this.canUpdateBooth()
		if (!canUpdate) {
			throw new Error('Booth is locked for a previous demo update')
		}
	}

	public async canUpdateBooth() {
		const url = '/api/updates/lock'
		const response = await axios.get(url)
		return response.data.locked === false
	}

	public async lockBoothToUpdates() {
		const url = '/api/updates/lock'
		const response = await axios.post(url)
		return response.data
	}
}

export default new API()
